import React from 'react';
import { Link } from 'react-router-dom';
import { Compass, Ship, MapPin, ArrowRight, IdCard } from 'lucide-react';

const FOUNDER_PHOTO = "https://customer-assets-cm19k8pv.emergentagent.net/job_visual-web-gen-1/artifacts/it20bf1t_40087A92-E002-445A-B4D1-06E03B0ADC3F_1_105_c.jpeg";

const activities = [
  {
    n: '1',
    icon: Compass,
    title: 'Balade lagunaire',
    desc: "Sorties d'une à deux heures sur la lagune et le lac Nokoué.",
  },
  {
    n: '2',
    icon: Ship,
    title: 'Transport fluvio-lagunaire',
    desc: 'Un vrai canal de déplacement entre Porto-Novo, Cotonou et les villages du lac.',
  },
  {
    n: '3',
    icon: MapPin,
    title: 'Tourisme',
    desc: 'Circuits, privatisations et accompagnement des visiteurs.',
  },
];

const Concept = () => {
  return (
    <section id="concept" className="bg-white py-20 px-6" data-testid="concept-section">
      <div className="max-w-7xl mx-auto">
        {/* Titre centré */}
        <div className="text-center mb-14">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900" data-testid="concept-title">
            Le concept
          </h2>
          <div className="w-24 h-1 bg-orange-500 rounded-full mx-auto mt-4"></div>
        </div>

        {/* Grille 2 colonnes : texte + activités */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Colonne gauche — Texte */}
          <div className="space-y-5 text-slate-700 leading-relaxed text-base" data-testid="concept-text">
            <p>
              Pierre & Edois est le concept par lequel la société{' '}
              <strong className="text-slate-900">Onyx Lord Services</strong> entend réconcilier la
              population béninoise avec le tourisme interne.
            </p>
            <p>
              Chez OLS, nous sommes convaincus que le développement d'un pays passe par le
              patriotisme — et qui dit patriotisme dit amour de sa terre.
            </p>
            <p>
              Par ce projet, nous espérons redonner le goût de la beauté naturelle de nos fleuves
              et de nos lagunes, et ouvrir en même temps un nouveau canal de transport et un
              nouveau mode de vie pour les Béninois.
            </p>
            <p className="text-orange-600 font-semibold pt-2">— OLS · Visons plus loin</p>
          </div>

          {/* Colonne droite — Nos activités + Carte de membre */}
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-bold text-slate-900 mb-6" data-testid="concept-activities-title">
                Nos activités
              </h3>
              <div className="space-y-6">
                {activities.map((a) => {
                  const Icon = a.icon;
                  return (
                    <div
                      key={a.n}
                      className="flex gap-4 group"
                      data-testid={`concept-activity-${a.n}`}
                    >
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center font-bold text-sm border border-orange-200 group-hover:bg-orange-500 group-hover:text-white transition-all">
                        {a.n}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className="w-4 h-4 text-orange-500" />
                          <h4 className="font-bold text-slate-900">{a.title}</h4>
                        </div>
                        <p className="text-slate-600 text-sm leading-relaxed">{a.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Carte de membre — teaser */}
            <Link
              to="/carte-membre"
              className="block border-2 border-dashed border-orange-400 rounded-2xl p-5 hover:bg-orange-50 hover:border-orange-500 transition-colors group"
              data-testid="concept-carte-membre-link"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <IdCard className="w-6 h-6 text-orange-500" />
                  <div>
                    <div className="font-bold text-slate-900">Carte de membre</div>
                    <div className="text-sm text-slate-600">
                      Rejoignez notre communauté et profitez d'avantages exclusifs
                    </div>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-orange-500 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          </div>
        </div>

        {/* Section fondateur en bas */}
        <div
          className="mt-20 pt-14 border-t border-slate-200"
          data-testid="concept-founder-block"
        >
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12 max-w-4xl mx-auto">
            <div className="relative flex-shrink-0">
              <div className="w-40 h-40 md:w-48 md:h-48 rounded-full overflow-hidden ring-4 ring-orange-100 shadow-xl">
                <img
                  src={FOUNDER_PHOTO}
                  alt="Onyx-Lord Vignikin FANOU"
                  className="w-full h-full object-cover"
                  data-testid="concept-founder-photo"
                />
              </div>
              <div className="absolute -bottom-2 -right-2 bg-orange-500 text-white rounded-full px-3 py-1 text-xs font-bold shadow-lg">
                CEO
              </div>
            </div>

            <div className="text-center md:text-left flex-1" data-testid="concept-founder-info">
              <div className="text-sm uppercase tracking-widest text-orange-500 font-semibold mb-2">
                Fondateur & CEO
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
                Onyx-Lord Vignikin FANOU
              </h3>
              <p className="text-slate-600 leading-relaxed">
                CEO du Groupe <strong className="text-slate-900">OLS</strong> et de{' '}
                <strong className="text-slate-900">Pierre &amp; Edois</strong>.
              </p>
              <p className="text-slate-600 mt-2 text-sm leading-relaxed">
                Expert Informatique · Chef de Projet Senior · Manager d'Équipe Certifié
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Concept;
